/**
 * Build the checkout "order_data" payload from the current in-progress selections on the
 * cart/checkout page. Used for two purposes that need different tolerance for missing data:
 *
 * - Live FETCH/LIST calls (cart summary, delivery/payment options, delivery option form) made
 *   as the customer interacts with the page, before everything has necessarily been filled in -
 *   so campaign conditions (billing/shipping country, delivery/payment method, cart totals...)
 *   can be re-evaluated against whatever is currently known, and the response (summary totals,
 *   unlocked/locked campaigns) reflects the live state. Missing pieces resolve to null - the
 *   backend's CheckoutPostResolve treats every field as optional for these endpoints.
 * - The final POST /one-page submit, where billingAddress/shippingAddress/delivery_method/
 *   payment_method are guaranteed present (validated by the caller before building this object).
 *
 * Keeping one field-mapping source for both avoids the two representations drifting apart.
 */
export const buildCheckoutOrderData = ({ form, billingAddress, shippingAddress, selectedDeliveryOption, deliveryOptionFormFields }) => ({
    id_company_billing: billingAddress?.id_company ?? null,
    id_company_address_billing: billingAddress?.id_company_address ?? null,
    sinhro_id_company_address_billing: billingAddress?.sinhro_id_company_address ?? null,
    company_name_billing: billingAddress?.company_name ?? null,
    pib_billing: billingAddress?.pib ?? null,
    maticni_broj_billing: billingAddress?.maticni_broj ?? null,
    first_name_billing: billingAddress?.first_name ?? null,
    last_name_billing: billingAddress?.last_name ?? null,
    phone_billing: billingAddress?.phone ?? null,
    email_billing: billingAddress?.email ?? null,
    address_billing: billingAddress?.address ?? null,
    object_number_billing: billingAddress?.object_number ?? null,
    floor_billing: billingAddress?.floor ?? null,
    apartment_number_billing: billingAddress?.apartment_number ?? null,
    id_town_billing: billingAddress?.id_town ?? null,
    town_name_billing: billingAddress?.town_name ?? null,
    zip_code_billing: billingAddress?.zip_code ?? null,
    id_municipality_billing: billingAddress?.id_municipality ?? null,
    municipality_name_billing: billingAddress?.municipality_name ?? null,
    id_country_billing: billingAddress?.id_country ?? null,
    country_name_billing: billingAddress?.country_name ?? null,
    note_billing: billingAddress?.note ?? null,

    id_company_shipping: shippingAddress?.id_company ?? null,
    id_company_address_shipping: shippingAddress?.id_company_address ?? null,
    sinhro_id_company_address_shipping: shippingAddress?.sinhro_id_company_address ?? null,
    company_name_shipping: shippingAddress?.company_name ?? null,
    pib_shipping: shippingAddress?.pib ?? null,
    maticni_broj_shipping: shippingAddress?.maticni_broj ?? null,
    first_name_shipping: shippingAddress?.first_name ?? null,
    last_name_shipping: shippingAddress?.last_name ?? null,
    phone_shipping: shippingAddress?.phone ?? null,
    email_shipping: shippingAddress?.email ?? null,
    address_shipping: shippingAddress?.address ?? null,
    object_number_shipping: shippingAddress?.object_number ?? null,
    floor_shipping: shippingAddress?.floor ?? null,
    apartment_number_shipping: shippingAddress?.apartment_number ?? null,
    id_town_shipping: shippingAddress?.id_town ?? null,
    town_name_shipping: shippingAddress?.town_name ?? null,
    zip_code_shipping: shippingAddress?.zip_code ?? null,
    id_municipality_shipping: shippingAddress?.id_municipality ?? null,
    municipality_name_shipping: shippingAddress?.municipality_name ?? null,
    id_country_shipping: shippingAddress?.id_country ?? null,
    country_name_shipping: shippingAddress?.country_name ?? null,
    note_shipping: shippingAddress?.note ?? null,

    delivery_method: form.delivery_method ?? null,
    delivery_method_options: deliveryOptionFormFields?.length
        ? [
              {
                  id: form.delivery_method,
                  name: selectedDeliveryOption?.name ?? form.delivery_method,
                  data: Object.entries(form.delivery_method_values ?? {})
                      .filter(([, value]) => value !== null && value !== undefined && value !== "")
                      .map(([prop_name, selected]) => ({ id: prop_name, selected })),
              },
          ]
        : [],

    payment_method: form.payment_method ?? null,
    payment_method_options: [],

    note: form.note ?? null,
});
